import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Row, Col, Card, Badge } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import { CREATE_PRODUCT, UPDATE_PRODUCT } from '../../graphql/operations';

interface Choice {
  label: string;
  extraCost: number;
}

interface ProductOption {
  name: string;
  label: string;
  type: 'select' | 'radio' | 'number';
  choices?: Choice[];
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

interface ProductModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (message: string) => void;
  product: any | null; // Null for Add, non-null for Edit
}

const ProductModal: React.FC<ProductModalProps> = ({ show, onHide, onSuccess, product }) => {
  const [createProduct, { loading: creating }] = useMutation(CREATE_PRODUCT);
  const [updateProduct, { loading: updating }] = useMutation(UPDATE_PRODUCT);

  const isEdit = !!product;

  // Form Fields
  const [productId, setProductId] = useState('');
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('fas fa-print');
  const [category, setCategory] = useState('General');
  const [desc, setDesc] = useState('');
  const [basePrice, setBasePrice] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);
  const [options, setOptions] = useState<ProductOption[]>([]);

  // Local state for editing/adding an option
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
  const [showOptionForm, setShowOptionForm] = useState(false);
  const [optName, setOptName] = useState('');
  const [optLabel, setOptLabel] = useState('');
  const [optType, setOptType] = useState<'select' | 'radio' | 'number'>('select');
  const [optChoices, setOptChoices] = useState<Choice[]>([]);
  const [optMin, setOptMin] = useState<string>('');
  const [optMax, setOptMax] = useState<string>('');
  const [optStep, setOptStep] = useState<string>('');
  const [optUnit, setOptUnit] = useState('');

  // Choice inputs
  const [newChoiceLabel, setNewChoiceLabel] = useState('');
  const [newChoiceCost, setNewChoiceCost] = useState<number>(0);

  // Errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (show) {
      if (product) {
        setProductId(product.productId);
        setName(product.name);
        setIcon(product.icon || 'fas fa-print');
        setCategory(product.category || 'General');
        setDesc(product.desc || '');
        setBasePrice(product.basePrice || 0);
        setIsActive(product.isActive !== false);
        
        // Deep copy options
        const mappedOptions = (product.options || []).map((opt: any) => ({
          name: opt.name,
          label: opt.label,
          type: opt.type,
          min: opt.min,
          max: opt.max,
          step: opt.step,
          unit: opt.unit,
          choices: (opt.choices || []).map((c: any) => ({
            label: c.label,
            extraCost: c.extraCost,
          })),
        }));
        setOptions(mappedOptions);
      } else {
        setProductId('');
        setName('');
        setIcon('fas fa-print');
        setCategory('General');
        setDesc('');
        setBasePrice(0);
        setIsActive(true);
        setOptions([]);
      }
      setEditingOptionIndex(null);
      setShowOptionForm(false);
      setErrors({});
      setSubmitError('');
    }
  }, [show, product]);

  const validateProduct = () => {
    const newErrors: Record<string, string> = {};

    if (!productId.trim()) {
      newErrors.productId = 'Product ID is required';
    } else if (!/^[a-z0-9\-]+$/.test(productId)) {
      newErrors.productId = 'Product ID must be lowercase, numbers, and dashes only';
    }

    if (!name.trim()) {
      newErrors.name = 'Product Name is required';
    }

    if (basePrice < 0) {
      newErrors.basePrice = 'Base price must be greater than or equal to 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddChoice = () => {
    if (!newChoiceLabel.trim()) return;
    setOptChoices([...optChoices, { label: newChoiceLabel.trim(), extraCost: newChoiceCost }]);
    setNewChoiceLabel('');
    setNewChoiceCost(0);
  };

  const handleRemoveChoice = (index: number) => {
    setOptChoices(optChoices.filter((_, idx) => idx !== index));
  };

  const handleOpenOptionForm = (index: number | null) => {
    if (index !== null) {
      // Editing existing option
      const opt = options[index];
      setOptName(opt.name);
      setOptLabel(opt.label);
      setOptType(opt.type);
      setOptMin(opt.min !== undefined ? String(opt.min) : '');
      setOptMax(opt.max !== undefined ? String(opt.max) : '');
      setOptStep(opt.step !== undefined ? String(opt.step) : '');
      setOptUnit(opt.unit || '');
      setOptChoices(opt.choices ? [...opt.choices] : []);
      setEditingOptionIndex(index);
    } else {
      // Adding new option
      setOptName('');
      setOptLabel('');
      setOptType('select');
      setOptMin('');
      setOptMax('');
      setOptStep('');
      setOptUnit('');
      setOptChoices([]);
      setEditingOptionIndex(null);
    }
    setShowOptionForm(true);
  };

  const handleSaveOption = () => {
    if (!optName.trim() || !optLabel.trim()) {
      alert('Option Name and Label are required.');
      return;
    }

    const optionToSave: ProductOption = {
      name: optName.trim(),
      label: optLabel.trim(),
      type: optType,
    };

    if (optType === 'number') {
      if (optMin) optionToSave.min = parseFloat(optMin);
      if (optMax) optionToSave.max = parseFloat(optMax);
      if (optStep) optionToSave.step = parseFloat(optStep);
      if (optUnit) optionToSave.unit = optUnit.trim();
    } else {
      optionToSave.choices = optChoices;
    }

    if (editingOptionIndex !== null) {
      const updatedOptions = [...options];
      updatedOptions[editingOptionIndex] = optionToSave;
      setOptions(updatedOptions);
    } else {
      setOptions([...options, optionToSave]);
    }

    setShowOptionForm(false);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateProduct()) return;
    setSubmitError('');

    // Format options for backend
    const formattedOptions = options.map(opt => {
      const formatted: any = {
        name: opt.name,
        label: opt.label,
        type: opt.type,
      };
      if (opt.type === 'number') {
        if (opt.min !== undefined) formatted.min = opt.min;
        if (opt.max !== undefined) formatted.max = opt.max;
        if (opt.step !== undefined) formatted.step = opt.step;
        if (opt.unit !== undefined) formatted.unit = opt.unit;
      } else {
        formatted.choices = (opt.choices || []).map(c => ({
          label: c.label,
          extraCost: c.extraCost,
        }));
      }
      return formatted;
    });

    try {
      if (isEdit) {
        const { data } = await updateProduct({
          variables: {
            id: product.id,
            productId: productId.trim().toLowerCase(),
            name: name.trim(),
            icon: icon.trim(),
            category: category.trim(),
            desc: desc.trim(),
            basePrice: parseFloat(String(basePrice)),
            isActive,
            options: formattedOptions,
          },
        });

        if (data?.updateProduct?.success) {
          onSuccess(data.updateProduct.message || 'Product updated successfully!');
          onHide();
        } else {
          setSubmitError(data?.updateProduct?.message || 'Failed to update product.');
        }
      } else {
        const { data } = await createProduct({
          variables: {
            productId: productId.trim().toLowerCase(),
            name: name.trim(),
            icon: icon.trim(),
            category: category.trim(),
            desc: desc.trim(),
            basePrice: parseFloat(String(basePrice)),
            isActive,
            options: formattedOptions,
          },
        });

        if (data?.createProduct?.success) {
          onSuccess(data.createProduct.message || 'Product created successfully!');
          onHide();
        } else {
          setSubmitError(data?.createProduct?.message || 'Failed to create product.');
        }
      }
    } catch (err: any) {
      setSubmitError(err.message || 'An error occurred during submission.');
    }
  };

  const submitting = creating || updating;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-bottom pb-3" style={{ background: 'linear-gradient(135deg, #f8f9ff, #ffffff)' }}>
        <Modal.Title className="fw-bold text-dark" style={{ fontSize: '18px' }}>
          <i className="fas fa-boxes text-primary me-2" />
          {isEdit ? 'Modify Product Information' : 'Add New Product to Catalogue'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ padding: '24px' }}>
          {submitError && (
            <div className="alert alert-danger mb-4 py-2 px-3 rounded-3 border-0" style={{ fontSize: '13px' }}>
              <i className="fas fa-exclamation-triangle me-2" />
              {submitError}
            </div>
          )}

          <Row className="g-4">
            {/* Left Column: Product Details */}
            <Col lg={6}>
              <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                <i className="fas fa-info-circle text-primary" />
                Product Details
              </h6>
              
              <div className="bg-light rounded-4 p-4 border mb-4">
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Product ID *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. business-cards"
                        value={productId}
                        onChange={(e) => setProductId(e.target.value)}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                        isInvalid={!!errors.productId}
                        disabled={isEdit} // Do not allow editing productId easily
                      />
                      <Form.Control.Feedback type="invalid">{errors.productId}</Form.Control.Feedback>
                      <small className="text-muted" style={{ fontSize: '11px' }}>Lowercase, numbers, and dashes.</small>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Product Name *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Standard Business Cards"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                        isInvalid={!!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Base Price (₹) *</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={basePrice}
                        onChange={(e) => setBasePrice(parseFloat(e.target.value) || 0)}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                        isInvalid={!!errors.basePrice}
                      />
                      <Form.Control.Feedback type="invalid">{errors.basePrice}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Category</Form.Label>
                      <Form.Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                      >
                        <option value="General">General</option>
                        <option value="Printing">Printing</option>
                        <option value="Apparel">Apparel</option>
                        <option value="Accessories">Accessories</option>
                        <option value="Marketing Materials">Marketing Materials</option>
                        <option value="Signage">Signage</option>
                        <option value="Packaging">Packaging</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Icon Class</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="e.g. fas fa-address-card"
                          value={icon}
                          onChange={(e) => setIcon(e.target.value)}
                          style={{ borderRadius: '8px', fontSize: '13px' }}
                        />
                        <div className="d-flex align-items-center justify-content-center bg-white border rounded-3" style={{ width: '40px', height: '38px', fontSize: '16px' }}>
                          <i className={icon || 'fas fa-print'} />
                        </div>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="A brief explanation of the product shown to customers..."
                        value={desc}
                        onChange={(e) => setDesc(e.target.value)}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group className="d-flex align-items-center justify-content-between bg-white border p-3 rounded-3 mt-2">
                      <div>
                        <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Public Listing Status</div>
                        <div className="text-muted" style={{ fontSize: '11.5px' }}>Determine if this product should be listed and ordering enabled on the public page.</div>
                      </div>
                      <Form.Check
                        type="switch"
                        id="isActive-switch"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        style={{ transform: 'scale(1.2)' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Right Column: Options Config */}
            <Col lg={6}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  <i className="fas fa-sliders-h text-primary" />
                  Product Options ({options.length})
                </h6>
                {!showOptionForm && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="fw-semibold py-1 px-3 d-flex align-items-center gap-1"
                    style={{ borderRadius: '6px', fontSize: '12px' }}
                    onClick={() => handleOpenOptionForm(null)}
                  >
                    <i className="fas fa-plus" /> Add Option
                  </Button>
                )}
              </div>

              {showOptionForm ? (
                /* Add/Edit Option Sub-Form */
                <Card className="border-primary rounded-4 shadow-sm p-3 mb-4 bg-white">
                  <Card.Header className="bg-transparent border-0 fw-bold text-dark p-0 mb-3" style={{ fontSize: '14px' }}>
                    {editingOptionIndex !== null ? 'Modify Option' : 'Configure New Option'}
                  </Card.Header>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Option key name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. paperGSM"
                          value={optName}
                          onChange={(e) => setOptName(e.target.value)}
                          style={{ borderRadius: '6px', fontSize: '12.5px' }}
                        />
                        <small className="text-muted" style={{ fontSize: '10px' }}>Keys must match backend references (no spaces).</small>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Label for user *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Paper Thickness"
                          value={optLabel}
                          onChange={(e) => setOptLabel(e.target.value)}
                          style={{ borderRadius: '6px', fontSize: '12.5px' }}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Option Type</Form.Label>
                        <div className="d-flex gap-2">
                          {['select', 'radio', 'number'].map(t => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => setOptType(t as any)}
                              className={`btn py-1 px-3 flex-fill border ${optType === t ? 'btn-primary border-primary fw-semibold' : 'btn-outline-secondary bg-white text-muted'}`}
                              style={{ borderRadius: '6px', fontSize: '12px', textTransform: 'capitalize' }}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </Form.Group>
                    </Col>

                    {optType === 'number' ? (
                      /* Number Inputs Config */
                      <Col md={12}>
                        <Row className="g-2">
                          <Col xs={3}>
                            <Form.Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Min</Form.Label>
                            <Form.Control
                              type="number"
                              value={optMin}
                              onChange={(e) => setOptMin(e.target.value)}
                              style={{ borderRadius: '6px', fontSize: '12px' }}
                            />
                          </Col>
                          <Col xs={3}>
                            <Form.Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Max</Form.Label>
                            <Form.Control
                              type="number"
                              value={optMax}
                              onChange={(e) => setOptMax(e.target.value)}
                              style={{ borderRadius: '6px', fontSize: '12px' }}
                            />
                          </Col>
                          <Col xs={3}>
                            <Form.Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Step</Form.Label>
                            <Form.Control
                              type="number"
                              value={optStep}
                              onChange={(e) => setOptStep(e.target.value)}
                              style={{ borderRadius: '6px', fontSize: '12px' }}
                            />
                          </Col>
                          <Col xs={3}>
                            <Form.Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Unit</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="e.g. mm"
                              value={optUnit}
                              onChange={(e) => setOptUnit(e.target.value)}
                              style={{ borderRadius: '6px', fontSize: '12px' }}
                            />
                          </Col>
                        </Row>
                      </Col>
                    ) : (
                      /* Choices Config (For Select / Radio) */
                      <Col md={12}>
                        <Form.Label className="fw-semibold text-muted mb-2" style={{ fontSize: '12px' }}>Choices Values & Extra Cost (₹)</Form.Label>
                        <div className="bg-light p-2 rounded-3 border mb-2">
                          {optChoices.length === 0 ? (
                            <div className="text-center text-muted py-2" style={{ fontSize: '11.5px' }}>No options choices created yet.</div>
                          ) : (
                            <div className="d-flex flex-wrap gap-2">
                              {optChoices.map((c, i) => (
                                <Badge key={i} bg="primary" className="d-flex align-items-center gap-2 py-1 px-2 fw-normal" style={{ fontSize: '11.5px', borderRadius: '6px' }}>
                                  <span>{c.label} (+₹{c.extraCost})</span>
                                  <i className="fas fa-times cursor-pointer" style={{ cursor: 'pointer' }} onClick={() => handleRemoveChoice(i)} />
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="d-flex gap-2">
                          <Form.Control
                            type="text"
                            placeholder="Choice Label (e.g. 350 GSM)"
                            value={newChoiceLabel}
                            onChange={(e) => setNewChoiceLabel(e.target.value)}
                            style={{ borderRadius: '6px', fontSize: '12px', flex: 2 }}
                          />
                          <Form.Control
                            type="number"
                            placeholder="Extra cost (₹)"
                            value={newChoiceCost || ''}
                            onChange={(e) => setNewChoiceCost(parseFloat(e.target.value) || 0)}
                            style={{ borderRadius: '6px', fontSize: '12px', flex: 1 }}
                          />
                          <Button variant="outline-success" size="sm" style={{ borderRadius: '6px' }} onClick={handleAddChoice}>
                            Add
                          </Button>
                        </div>
                      </Col>
                    )}
                  </Row>
                  
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button variant="outline-secondary" size="sm" onClick={() => setShowOptionForm(false)} style={{ borderRadius: '6px' }}>
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSaveOption} style={{ borderRadius: '6px' }}>
                      Save Option
                    </Button>
                  </div>
                </Card>
              ) : (
                /* Options List */
                <div className="bg-light rounded-4 p-4 border" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {options.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-sliders-h mb-2" style={{ fontSize: '24px' }}></i>
                      <div>No custom options configured for this product. Only base price will apply.</div>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {options.map((opt, i) => (
                        <div key={i} className="bg-white p-3 rounded-3 border d-flex justify-content-between align-items-start">
                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>{opt.label}</span>
                              <Badge bg="secondary" style={{ fontSize: '10px', textTransform: 'uppercase' }}>{opt.type}</Badge>
                            </div>
                            <div className="text-muted" style={{ fontSize: '11px' }}>Key: {opt.name}</div>
                            
                            {opt.type === 'number' ? (
                              <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                                Range: {opt.min ?? 0} to {opt.max ?? 'N/A'} (Step: {opt.step ?? 1}) {opt.unit && `[${opt.unit}]`}
                              </div>
                            ) : (
                              <div className="d-flex flex-wrap gap-1 mt-2">
                                {opt.choices?.map((c, idx) => (
                                  <span key={idx} className="bg-light text-secondary border px-2 py-0.5 rounded" style={{ fontSize: '10.5px' }}>
                                    {c.label} (+₹{c.extraCost})
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              style={{ borderRadius: '4px', fontSize: '11px', padding: '2px 8px' }}
                              onClick={() => handleOpenOptionForm(i)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              style={{ borderRadius: '4px', fontSize: '11px', padding: '2px 8px' }}
                              onClick={() => handleRemoveOption(i)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <><Spinner size="sm" animation="border" className="me-2" />Saving...</>
            ) : (
              <><i className="fas fa-save me-2" />{isEdit ? 'Update Product' : 'Create Product'}</>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ProductModal;
